import { MutableRefObject, RefObject } from 'react';

import { PopoverSize, MarkerPopoverPositionTypes, PageContentTypes } from '../types';
import {
  ABOUT_LANGUAGE_PATH,
  AMENITIES_LANGUAGE_PATH,
  CONTENT_KEY,
  languages,
  LOCATION_LANGUAGE_PATH,
  NEWS_LANGUAGE_PATH,
  SITEPLAN_LANGUAGE_PATH,
} from '../commons/constants';
import { WindowSizeTypes } from '../hooks/useWindowSize';

export const changeLanguageUrl = (url: string, language: string): string => {
  if (!url) return '';
  if (!language) return url;

  let newUrl = url;
  // find language from url
  const currentLanguageUrl = Object.values(languages).find((languageItem) => {
    const findLanguage = url.indexOf(`/${languageItem.locale}/`);
    return findLanguage !== -1;
  });

  // change language on url
  if (currentLanguageUrl) {
    newUrl = url.replace(`/${currentLanguageUrl.locale}/`, `/${language}/`);
  }

  const temp = new URL(newUrl);
  // add timestamp
  temp.searchParams.set('timestamp', `${new Date().getTime()}`);

  return temp.href;
};

export const sleep = (duration: number): Promise<void> => {
  return new Promise((resolve) => setTimeout(resolve, duration));
};

export const debounce = (f: (...props) => void, ms: number): any => {
  let timer = null;

  return (...args) => {
    const onComplete = () => {
      if (f) f.apply(this, args);
      timer = null;
    };

    if (timer) {
      clearTimeout(timer);
    }

    timer = setTimeout(onComplete, ms);
  };
};

export const scrollToElement = (id: string): void => {
  const element = document.getElementById(id);
  if (!element) return null;
  element.scrollIntoView();
};

export const getPopoverPosition = (props: {
  windowSize: WindowSizeTypes;
  markerSize?: number;
  popoverSize?: PopoverSize;
  offsetLeft?: number;
  offsetRight?: number;
  offsetTop?: number;
  ref: MutableRefObject<HTMLDivElement>;
}): MarkerPopoverPositionTypes => {
  const {
    windowSize,
    markerSize = 34,
    popoverSize,
    ref,
    offsetLeft = 0,
    offsetRight = 0,
    offsetTop = 0,
  } = props;
  const gapPopoverMarker = 16;
  const gapPopover = 20;

  let positionStyle: MarkerPopoverPositionTypes = {
    transform: 'translate(-50%, 0)',
    bottom: `calc(100% + ${gapPopoverMarker}px)`,
    left: '50%',
  };

  if (windowSize && windowSize.width && windowSize.height && ref && ref.current) {
    // get position point marker compare viewport
    const boundingClient = ref.current.getBoundingClientRect();
    // popover default--height: 244px,
    const popoverDefaultHeight = (popoverSize && popoverSize.height) || 244;
    // popover default--width: 240px
    const popoverDefaultWidth = (popoverSize && popoverSize.width) || 240;
    // 50% of popover width
    const popoverHalfWidth = popoverDefaultWidth / 2;
    const limitRight = windowSize.width - gapPopover - offsetRight;
    const limitLeft = popoverHalfWidth + offsetLeft;

    // check limit top to set popover show at bottom of marker
    // top-menu bar--height: 40px
    if (
      boundingClient.top - 40 <
      popoverDefaultHeight + gapPopoverMarker + gapPopover + offsetTop
    ) {
      positionStyle = {
        transform: 'translate(-50%, 0)',
        top: `calc(100% + ${markerSize + gapPopoverMarker}px `,
        left: '50%',
      };
    }

    // check limit right to set popover show full in screen
    if (boundingClient.right + popoverHalfWidth > limitRight) {
      positionStyle = {
        transform: `translate(calc(-50% - ${
          boundingClient.right + popoverHalfWidth - limitRight
        }px), 0)`,
        bottom: !positionStyle.top ? `calc(100% + ${gapPopoverMarker}px)` : null,
        left: '50%',
        top: positionStyle.top,
      };
    }

    // check limit left to set popover show full in screen
    if (boundingClient.left < limitLeft) {
      positionStyle = {
        transform: `translate(calc(-50% - ${boundingClient.left - gapPopover - limitLeft}px), 0)`,
        bottom: !positionStyle.top ? `calc(100% + ${gapPopoverMarker}px)` : null,
        left: '50%',
        top: positionStyle.top,
      };
    }
  }
  return positionStyle;
};

export const getRelativePathUrl = (url: string): string => {
  const hasContextPath = checkHasContextPath();

  if (hasContextPath) {
    return `${getContextPath()}${url}`;
  }

  return url;
};

export const getContextPath = (): string => {
  return window.location.pathname.substring(0, window.location.pathname.indexOf('/', 2));
};

export const checkHasContextPath = (): boolean => {
  return (
    [
      process.env.REACT_APP_CONTEXT_PATH_EN,
      process.env.REACT_APP_CONTEXT_PATH_VI,
      process.env.REACT_APP_CONTEXT_PATH_JP,
    ].indexOf(getContextPath()) !== -1
  );
};

export const isImage = (url: string): boolean => {
  return /\.(jpg|jpeg|png|webp|avif|gif|svg)$/.test(url);
};

export const getActiveContentKey = (contentKey: string): string => {
  switch (contentKey) {
    case ABOUT_LANGUAGE_PATH.VI:
    case ABOUT_LANGUAGE_PATH.EN:
    case ABOUT_LANGUAGE_PATH.JP:
      return CONTENT_KEY.ABOUT.key;
    case LOCATION_LANGUAGE_PATH.VI:
    case LOCATION_LANGUAGE_PATH.EN:
    case LOCATION_LANGUAGE_PATH.JP:
      return CONTENT_KEY.LOCATION.key;
    case AMENITIES_LANGUAGE_PATH.VI:
    case AMENITIES_LANGUAGE_PATH.EN:
    case AMENITIES_LANGUAGE_PATH.JP:
      return CONTENT_KEY.AMENITIES.key;
    case SITEPLAN_LANGUAGE_PATH.VI:
    case SITEPLAN_LANGUAGE_PATH.EN:
    case SITEPLAN_LANGUAGE_PATH.JP:
      return CONTENT_KEY.SITEPLAN.key;
    case NEWS_LANGUAGE_PATH.VI:
    case NEWS_LANGUAGE_PATH.EN:
    case NEWS_LANGUAGE_PATH.JP:
      return CONTENT_KEY.NEWS.key;

    default:
      return '';
  }
};

// default type is enum language path
export const getLanguagePathByContent = (content: PageContentTypes): any => {
  switch (content.key) {
    case CONTENT_KEY.SITEPLAN.key:
      return SITEPLAN_LANGUAGE_PATH;
    case CONTENT_KEY.AMENITIES.key:
      return AMENITIES_LANGUAGE_PATH;
    case CONTENT_KEY.LOCATION.key:
      return LOCATION_LANGUAGE_PATH;
    case CONTENT_KEY.ABOUT.key:
      return ABOUT_LANGUAGE_PATH;

    default:
      return null;
  }
};

export const getTransformValue = (
  e: RefObject<HTMLElement>
): { transformX: number; transformY: number } => {
  const style = window.getComputedStyle(e.current);
  const matrix = new WebKitCSSMatrix(style.transform);
  const { m42, m41 } = matrix;
  // https://askcodes.net/coding/how-to-get-value-translatex-by-javascript

  return { transformX: m41, transformY: m42 };
};

export const isInIframe = (): boolean => {
  try {
    return window.self !== window.top;
  } catch (e) {
    return true;
  }
};
